import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import { ChevronLeft, ChevronRight, Droplet, Zap } from "lucide-react";
import { getCalendar, getPredictions, getCycleForecast } from "../lib/api";
import { fmtDate, phaseLabel } from "../lib/format";
import LogSheets from "../components/LogSheets";
import HormoneTimeline from "../components/HormoneTimeline";
import { motion } from "framer-motion";
import { StarSticker, BlobSticker, CrescentSticker, FlowerSticker } from "../components/DecorativeShapes";

const weekday = ["S", "M", "T", "W", "T", "F", "S"];

function colorForTag(tag) {
  switch (tag) {
    case "period":    return "hsl(var(--phase-period))";
    case "ovulation": return "hsl(var(--phase-ovulation))";
    case "fertile":   return "hsl(var(--phase-fertile))";
    default:          return null;
  }
}

const PHASE_COLORS = {
  menstrual:    "#F88B8B",
  follicular:   "#F5D867",
  ovulation:    "#FF8FB8",
  luteal_early: "#C3B1E1",
  luteal:       "#A89CCC",
  luteal_late:  "#8D84B3",
};

function ForecastStrip({ forecast }) {
  if (!forecast?.has_data) return null;
  return (
    <div className="surface-ivory plush-shadow-sm rounded-[24px] p-4" data-testid="cycle-forecast-strip">
      <p className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-60 mb-3">7-day energy forecast</p>
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
        {forecast.days.map((d) => (
          <div
            key={d.date}
            className={`flex flex-col items-center gap-1 min-w-[44px] rounded-[16px] p-2 transition ${d.is_today ? "ring-2 ring-[hsl(var(--plum))] ring-offset-1 ring-offset-[hsl(var(--card))]" : ""}`}
            style={{ background: PHASE_COLORS[d.phase] + "28" }}
            data-testid={`forecast-day-${d.day_label}`}
          >
            <span className="text-[9px] uppercase font-bold opacity-60">{d.day_label}</span>
            {/* Energy bar */}
            <div className="w-5 bg-[hsl(var(--muted))] rounded-full overflow-hidden" style={{ height: 28 }}>
              <div
                className="w-full rounded-full transition-all"
                style={{
                  height: `${(d.energy / 10) * 100}%`,
                  background: PHASE_COLORS[d.phase] || "#F88B8B",
                  marginTop: `${((10 - d.energy) / 10) * 100}%`,
                }}
              />
            </div>
            <span className="text-[9px] font-semibold opacity-70">{d.phase_label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CyclePage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [calendar, setCalendar] = useState(null);
  const [pred, setPred] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [cal, p, fc] = await Promise.all([getCalendar(year, month), getPredictions(), getCycleForecast()]);
        setCalendar(cal);
        setPred(p);
        setForecast(fc);
      } catch {}
    })();
  }, [year, month, refreshTick]);

  const monthLabel = useMemo(
    () => new Date(year, month - 1, 1).toLocaleString("default", { month: "long", year: "numeric" }),
    [year, month]
  );
  const firstWeekday = useMemo(() => new Date(year, month - 1, 1).getDay(), [year, month]);
  const prev = () => { const m = month - 1; if (m < 1) { setMonth(12); setYear(year - 1); } else setMonth(m); };
  const next = () => { const m = month + 1; if (m > 12) { setMonth(1); setYear(year + 1); } else setMonth(m); };

  return (
    <div className="kanya-page-pad-bottom" data-testid="cycle-page">
      <PageHeader
        eyebrow="Your cycle"
        title="Your rhythm"
        subtitle={pred?.has_data ? `${phaseLabel(pred.current_phase)} \u00b7 Day ${pred.day_of_cycle}` : "Log your first period to begin"}
      />

      <div className="px-5 space-y-4">
        {/* === EDITORIAL NEXT-PERIOD HERO === */}
        {pred?.has_data && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="surface-plum editorial-shadow relative overflow-hidden rounded-[32px] p-6"
            data-testid="cycle-next-period-card"
          >
            <BlobSticker size={120} color="#E5A8C0" className="absolute -top-10 -right-10 float-slow opacity-90" />
            <StarSticker size={32} color="#E9B86A" className="absolute top-6 right-24 float-mid" />
            <p className="relative text-[10px] uppercase tracking-[0.28em] opacity-70 font-semibold">Next period</p>
            <p className="relative font-editorial text-[42px] leading-[1.0] mt-2">{fmtDate(pred.next_period_date, "EEEE")}</p>
            <p className="relative font-editorial text-[26px] leading-tight mt-0.5 opacity-90">{fmtDate(pred.next_period_date, "MMM d")}</p>
            <div className="relative mt-4 chip chip-on-pink">
              {pred.days_until_next_period <= 0 ? "Expected today" : `in ${pred.days_until_next_period} days`}
            </div>
          </motion.div>
        )}

        {/* === HORMONE TIMELINE (cinematic story view) === */}
        <HormoneTimeline days={30} />

        {/* === 7-DAY ENERGY FORECAST === */}
        {forecast?.has_data && <ForecastStrip forecast={forecast} />}

        {/* === MONTH NAV === */}
        <div className="flex items-center justify-between surface-ivory rounded-full px-2 py-1.5 border border-[hsl(var(--border))]" data-testid="cycle-month-header">
          <button onClick={prev} aria-label="Previous month" className="p-2 rounded-full hover:bg-[hsl(var(--muted))]" data-testid="cycle-prev-month">
            <ChevronLeft size={16} />
          </button>
          <p className="font-editorial text-[20px]">{monthLabel}</p>
          <button onClick={next} aria-label="Next month" className="p-2 rounded-full hover:bg-[hsl(var(--muted))]" data-testid="cycle-next-month">
            <ChevronRight size={16} />
          </button>
        </div>

        {/* === CALENDAR === */}
        <div className="surface-ivory plush-shadow-sm rounded-[28px] p-4" data-testid="cycle-calendar">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekday.map((d, i) => (
              <div key={i} className="text-center text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`pad-${i}`} />)}
            {(calendar?.days || []).map((d) => {
              const color = colorForTag(d.tag);
              const isOv = d.tag === "ovulation";
              return (
                <motion.div
                  key={d.date}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: d.day * 0.005 }}
                  className={`relative aspect-square flex items-center justify-center rounded-2xl text-sm font-semibold ${d.is_today ? "ring-2 ring-[hsl(var(--plum))] ring-offset-2 ring-offset-[hsl(var(--card))]" : ""}`}
                  style={{
                    backgroundColor: color ? color : "hsl(var(--muted))",
                    color: color ? "hsl(36 50% 96%)" : "hsl(var(--foreground))",
                    opacity: d.is_future ? 0.78 : 1,
                  }}
                  data-testid={`cycle-day-${d.day}`}
                  data-tag={d.tag || "none"}
                >
                  {d.day}
                  {isOv && <span className="absolute -top-1 -right-1"><StarSticker size={14} color="#E9B86A" /></span>}
                </motion.div>
              );
            })}
            {!calendar?.has_data && (
              <div className="col-span-7 mt-2 text-center text-xs text-[hsl(var(--muted-foreground))]">
                No predictions yet. Log a period to see your fertile window & ovulation.
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
            <span className="chip" style={{ background: "hsl(var(--phase-period))", color: "white" }}>Period</span>
            <span className="chip chip-mint">Fertile</span>
            <span className="chip" style={{ background: "hsl(var(--phase-ovulation))", color: "white" }}>Ovulation</span>
          </div>
        </div>

        {/* === PHASE EDUCATION === */}
        {pred?.has_data ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="surface-cream plush-shadow-sm rounded-[28px] p-5 relative overflow-hidden"
              data-testid="cycle-phase-card"
            >
              <BlobSticker size={80} color="#E5A8C0" className="absolute -top-6 -right-6 float-slow opacity-80" />
              <p className="relative text-[10px] uppercase tracking-[0.28em] font-semibold opacity-60">{pred.phase_info?.title} phase</p>
              <p className="relative font-editorial text-[28px] leading-tight mt-2">{pred.phase_info?.subtitle}</p>
              <p className="relative text-sm opacity-80 mt-2 leading-relaxed font-body">{pred.phase_info?.description}</p>
              <p className="relative text-xs opacity-65 mt-3 font-body"><span className="font-semibold">Hormones:</span> {pred.phase_info?.hormones}</p>
              <div className="relative mt-3 flex flex-wrap gap-2">
                {(pred.phase_info?.tips || []).map((t, i) => (
                  <span key={i} className="chip chip-blush">{t}</span>
                ))}
              </div>
            </motion.div>

            <div className="surface-mint plush-shadow-sm rounded-[28px] p-5 relative overflow-hidden" data-testid="cycle-fertile-card">
              <FlowerSticker size={56} color="#FFFFFF" className="absolute -top-4 -right-4 float-mid opacity-60" />
              <p className="relative text-[10px] uppercase tracking-[0.28em] font-semibold opacity-60">Fertile window</p>
              <p className="relative font-editorial text-[24px] leading-tight mt-2">{fmtDate(pred.fertile_window_start, "MMM d")} — {fmtDate(pred.fertile_window_end, "MMM d")}</p>
              <p className="relative text-sm opacity-80 mt-1 font-body">Ovulation: {fmtDate(pred.ovulation_date, "EEE, MMM d")}</p>
            </div>
          </>
        ) : (
          <div className="surface-cream plush-shadow-sm rounded-[28px] p-5 relative overflow-hidden" data-testid="cycle-empty-state">
            <BlobSticker size={80} color="#E5C99D" className="absolute -top-4 -right-4 float-slow opacity-80" />
            <p className="relative font-editorial text-[26px] leading-tight">Let's learn your rhythm</p>
            <p className="relative text-sm opacity-75 mt-2 font-body">Log your first day and we'll quietly personalize predictions over time.</p>
          </div>
        )}

        <button className="btn-pill-dark w-full" onClick={() => setSheet("period")} data-testid="cycle-log-period-button">
          <Droplet size={16} /> Log period
        </button>
      </div>

      <LogSheets open={sheet} onClose={() => setSheet(null)} onLogged={() => setRefreshTick((t) => t + 1)} />
    </div>
  );
}
